import { z } from "zod";

export {
  parseOnboardingPagedBody,
} from "../../onboard-form/schemas/onboard-invitation-row.schema.ts";

/** One L1-verified onboarding employee row (field names aligned with legacy HR setup list). */
export const hrSetupEmployeeRowSchema = z
  .object({
    id: z.union([z.number(), z.string()]),
    firstName: z.string().nullish(),
    middleName: z.string().nullish(),
    lastName: z.string().nullish(),
    personalEmail: z.string().nullish(),
    phoneNumber: z.string().nullish(),
    gender: z.string().nullish(),
    ccEmail: z.string().nullish(),
  })
  .passthrough();

export type HrSetupEmployeeRow = z.infer<typeof hrSetupEmployeeRowSchema>;

const pagedBlockSchema = z
  .object({
    data: z.array(hrSetupEmployeeRowSchema),
    totalPages: z.number().optional(),
    totalElements: z.number().optional(),
  })
  .passthrough();

/** Parses paginated HR setup list responses (`{ data: { data, totalPages, totalElements } }`). */
export function parseHrSetupPagedBody(raw: unknown): {
  rows: HrSetupEmployeeRow[];
  totalPages: number;
  totalElements: number;
} {
  // Queue responses use Spring's content envelope. Preserve consultant fields
  // while normalizing only the shared table columns.
  if (raw && typeof raw === "object") {
    const root = raw as Record<string, unknown>;
    const block = (root.data && typeof root.data === "object" && !Array.isArray(root.data) ? root.data : root) as Record<string, unknown>;
    if (Array.isArray(block.content)) {
      const rows = block.content.map((item) => {
        const row = item as Record<string, unknown>;
        return hrSetupEmployeeRowSchema.parse({ ...row, id: row.consultantId ?? row.id, employeeType: row.consultantId != null ? "CONSULTANT" : row.employeeType ?? "REGULAR", personalEmail: row.personalEmail ?? row.email, phoneNumber: row.phoneNumber ?? row.phone });
      });
      return { rows, totalPages: typeof block.totalPages === "number" ? block.totalPages : 1, totalElements: typeof block.totalElements === "number" ? block.totalElements : rows.length };
    }
  }
  const empty = { rows: [] as HrSetupEmployeeRow[], totalPages: 1, totalElements: 0 };
  if (raw == null || typeof raw !== "object") return empty;

  const direct = pagedBlockSchema.safeParse(raw);
  if (direct.success) {
    return {
      rows: direct.data.data,
      totalPages: direct.data.totalPages ?? 1,
      totalElements: direct.data.totalElements ?? direct.data.data.length,
    };
  }

  const root = raw as Record<string, unknown>;
  const inner = root.data;
  if (inner != null && typeof inner === "object") {
    const parsed = pagedBlockSchema.safeParse(inner);
    if (parsed.success) {
      return {
        rows: parsed.data.data,
        totalPages: parsed.data.totalPages ?? 1,
        totalElements: parsed.data.totalElements ?? parsed.data.data.length,
      };
    }
    const asArray = z.array(hrSetupEmployeeRowSchema).safeParse(inner);
    if (asArray.success) {
      return {
        rows: asArray.data,
        totalPages: 1,
        totalElements: asArray.data.length,
      };
    }
  }
  return empty;
}
